import { useState } from 'react'
import { Icone } from '@/components/ui/Icone'
import type { CarteAppariement, CarteOption } from '@/lib/api/feed'
import type { RenduOptions } from '@/lib/content/inline'
import { RichText } from '@/lib/content/RichText'

/**
 * Une question d'APPARIEMENT — [DECISIONS §150].
 *
 * **Elle était rendue en choix multiple**, ce qui la vidait de sa substance :
 * six lignes déjà appariées, une case devant chacune, et l'exercice devenait un
 * vrai/faux à six lignes. Les distracteurs devaient alors recombiner deux
 * paires justes — « Molaires — couronne tranchante et racine simple » — ce qui
 * se repère sans rien savoir de l'anatomie.
 *
 * **On tape à gauche, puis on tape la réponse.** Le glisser-déposer est le
 * geste évident et le mauvais choix : sur un Moto G4 il se dispute avec le
 * défilement de la page, au clavier il n'existe pas, au lecteur d'écran il n'a
 * pas de nom. Le menu déroulant par ligne, lui, répète la liste entière à
 * chaque ligne — six énoncés longs rendus six fois — dans une fenêtre native
 * qui recouvre justement la ligne à laquelle on répond.
 *
 * Taper deux fois n'a aucun de ces défauts : chaque texte n'est écrit qu'une
 * fois, tout l'état se lit d'un regard, les cibles sont de vrais `<button>`
 * — donc atteignables à la tabulation — et le geste est le même à la souris, au
 * doigt et au clavier.
 *
 * **Les réponses portent une lettre.** C'est elle qui s'affiche dans la ligne
 * appariée, et elle qui rend l'annonce sonore lisible : « Molaires, associé
 * à B ». Sans elle, un lecteur d'écran relirait tout le texte de la réponse au
 * milieu de la ligne.
 *
 * **Aucun distracteur à écrire.** Les membres droits des autres paires sont
 * déjà les distracteurs de chacune — c'est ce que le rendu en choix multiple ne
 * savait pas faire.
 */
export function TableDAppariement({
  gauche,
  droite,
  options,
  desactivee,
  attendu,
  onChangement,
}: {
  /** La colonne des énoncés à apparier, dans l'ordre servi. */
  gauche: CarteOption[]
  /** Les réponses, mélangées à part : c'est ce second désordre qui fait la question. */
  droite: CarteAppariement[]
  options: RenduOptions
  desactivee: boolean
  /**
   * L'appariement attendu, une fois la réponse donnée — `undefined` pendant
   * une épreuve notée, où l'on ne dévoile rien (RG-3.11quater).
   */
  attendu?: Record<string, string> | undefined
  /** L'état courant : option de gauche vers jeton de droite. */
  onChangement: (paires: Record<string, string>) => void
}) {
  const [paires, setPaires] = useState<Record<string, string>>({})

  // La ligne de gauche en attente de sa réponse. Une seule à la fois : deux
  // sélections simultanées rendraient indéterminé ce que la tape suivante
  // apparie.
  const [enAttente, setEnAttente] = useState<number | null>(null)

  const lettre = (jeton: string): string => {
    const rang = droite.findIndex((d) => d.jeton === jeton)

    return rang < 0 ? '?' : String.fromCharCode(65 + rang)
  }

  const appliquer = (suivant: Record<string, string>): void => {
    setPaires(suivant)
    onChangement(suivant)
  }

  const choisirAGauche = (id: number): void => {
    if (desactivee) return

    // Retaper la ligne en attente l'annule : sans ce retour en arrière, une
    // tape par erreur oblige à apparier n'importe quoi pour se libérer.
    setEnAttente((avant) => (avant === id ? null : id))
  }

  /**
   * Y a-t-il moins de réponses que de lignes ? — [DECISIONS §263].
   *
   * Alors l'une d'elles sert forcément deux fois : le juge de paix et le
   * commissaire du gouvernement relèvent tous deux du pouvoir judiciaire, et
   * quarante-huit appariements du corpus posent ce cas. La colonne droite ne
   * porte qu'une entrée par MEMBRE, jamais une par paire.
   */
  const partageable = droite.length < gauche.length

  const choisirADroite = (jeton: string): void => {
    if (desactivee || enAttente === null) return

    const suivant: Record<string, string> = {}

    /*
     * **Une réponse ne sert qu'une fois — quand il y en a autant que de
     * lignes.** Laisser réutiliser le même membre droit dans un appariement
     * un-pour-un produirait une réponse qui ne peut pas être juste, et le
     * candidat le découvrirait à la correction : on libère donc la ligne qui
     * l'occupait — « en fait c'est plutôt celle-ci ».
     *
     * Quand les réponses sont moins nombreuses que les lignes, cette libération
     * rendrait la question INSOLUBLE : il faudrait vider une ligne pour en
     * remplir une autre, indéfiniment.
     */
    for (const [cle, valeur] of Object.entries(paires)) {
      if (partageable || valeur !== jeton) suivant[cle] = valeur
    }

    suivant[String(enAttente)] = jeton
    appliquer(suivant)
    setEnAttente(null)
  }

  const detacher = (id: number): void => {
    if (desactivee) return

    const suivant: Record<string, string> = {}

    for (const [cle, valeur] of Object.entries(paires)) {
      if (cle !== String(id)) suivant[cle] = valeur
    }

    appliquer(suivant)
    setEnAttente(null)
  }

  const prises = new Set(Object.values(paires))

  return (
    <div className="mt-3 flex flex-col gap-3">
      <ul className="flex list-none flex-col gap-2">
        {gauche.map((ligne) => {
          const jeton = paires[String(ligne.id)]
          const choisie = enAttente === ligne.id
          // Le verdict ligne à ligne : il n'apparaît qu'une fois l'appariement
          // attendu connu, donc jamais pendant une épreuve.
          const juste =
            attendu === undefined || jeton === undefined
              ? null
              : attendu[String(ligne.id)] === jeton

          return (
            <li key={ligne.id} className="flex items-stretch gap-2">
              <button
                type="button"
                disabled={desactivee}
                aria-pressed={choisie}
                onClick={() => {
                  choisirAGauche(ligne.id)
                }}
                style={{
                  borderColor: choisie
                    ? 'var(--accent-fort)'
                    : juste === true
                      ? 'var(--juste)'
                      : juste === false
                        ? 'var(--bordure-champ)'
                        : 'var(--bordure)',
                  background:
                    juste === true
                      ? 'color-mix(in srgb, var(--juste) 14%, transparent)'
                      : juste === false
                        ? 'color-mix(in srgb, var(--texte-2) 16%, transparent)'
                        : choisie
                          ? 'color-mix(in srgb, var(--accent) 12%, transparent)'
                          : undefined,
                }}
                // RG-D10 — 44 px de haut au minimum : c'est la taille sous
                // laquelle une cible devient difficile à atteindre au pouce.
                className="flex min-h-11 flex-1 items-center gap-3 rounded-2xl border px-4 py-2 text-left text-[15px]"
              >
                <RichText
                  texte={ligne.content_md}
                  options={options}
                  className="min-w-0 flex-1 [&_p]:my-0"
                />

                {/* La case de droite : ce que la ligne porte, ou ce qu'elle
                    attend. Elle ne dit jamais rien de plus que la lettre — le
                    texte de la réponse est déjà à l'écran, sous la liste. */}
                <span
                  aria-hidden="true"
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border text-[15px] font-bold tabular-nums"
                  style={{
                    borderColor: jeton === undefined ? 'var(--bordure-champ)' : 'var(--accent-fort)',
                    color: jeton === undefined ? 'var(--texte-2)' : 'var(--accent-fort)',
                    borderStyle: jeton === undefined ? 'dashed' : 'solid',
                  }}
                >
                  {jeton === undefined ? '–' : lettre(jeton)}
                </span>
              </button>

              {/* Défaire une ligne sans la retaper : deux tapes sur la même
                  cible feraient un basculement que rien n'annonce. */}
              {jeton !== undefined && !desactivee ? (
                <button
                  type="button"
                  onClick={() => {
                    detacher(ligne.id)
                  }}
                  aria-label={`Défaire l’association de « ${resume(ligne.content_md)} »`}
                  className="flex w-11 shrink-0 items-center justify-center rounded-2xl border"
                  style={{ borderColor: 'var(--bordure)', color: 'var(--texte-2)' }}
                >
                  <Icone nom="croix" taille={16} />
                </button>
              ) : null}
            </li>
          )
        })}
      </ul>

      {/* **L'état de la sélection se DIT.** Sans cette ligne, un lecteur
          d'écran annonce le passage sur les réponses sans jamais dire à quoi
          elles vont s'attacher, et l'écran ne montre qu'une bordure. */}
      <p role="status" className="text-[13px]" style={{ color: 'var(--texte-2)' }}>
        {desactivee
          ? ''
          : enAttente === null
            ? 'Touchez une ligne, puis sa réponse.'
            : `« ${resume(texteDe(gauche, enAttente))} » — choisissez sa réponse.`}
      </p>

      <ul className="flex list-none flex-col gap-2">
        {droite.map((reponse, rang) => {
          const prise = prises.has(reponse.jeton)

          return (
            <li key={reponse.jeton}>
              <button
                type="button"
                disabled={desactivee || enAttente === null}
                onClick={() => {
                  choisirADroite(reponse.jeton)
                }}
                style={{
                  borderColor: prise ? 'var(--accent)' : 'var(--bordure)',
                  // Une réponse déjà prise reste cliquable — elle change alors
                  // de ligne — mais elle s'efface : ce qui reste à placer doit
                  // ressortir. Sauf quand elle est PARTAGEABLE : l'estomper
                  // découragerait le geste qu'il faut justement poser.
                  opacity: prise && enAttente !== null && !partageable ? 0.55 : 1,
                }}
                className="flex min-h-11 w-full items-center gap-3 rounded-2xl border px-4 py-2 text-left text-[15px] disabled:opacity-40"
              >
                <span
                  aria-hidden="true"
                  className="flex size-7 shrink-0 items-center justify-center rounded-full text-[13px] font-bold"
                  style={{ background: 'var(--surface-2)', color: 'var(--texte-1)' }}
                >
                  {String.fromCharCode(65 + rang)}
                </span>

                <RichText
                  texte={reponse.content_md}
                  options={options}
                  className="min-w-0 flex-1 [&_p]:my-0"
                />
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function texteDe(gauche: CarteOption[], id: number): string {
  return gauche.find((o) => o.id === id)?.content_md ?? ''
}

/** De quoi nommer une ligne dans un libellé sonore, sans y verser un paragraphe. */
function resume(texte: string): string {
  const propre = texte.replace(/[*_`#]/g, '').trim()

  return propre.length > 60 ? `${propre.slice(0, 60)}…` : propre
}
